import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faNotesMedical } from '@fortawesome/free-solid-svg-icons';
import { type Patient, clinicsService } from '../../services/clinicsService';

interface ClinicsPatientNotesModalProps {
  patient: Patient;
  onClose: () => void;
  onSaveSuccess: () => void;
}

const ClinicsPatientNotesModal: React.FC<ClinicsPatientNotesModalProps> = ({ patient, onClose, onSaveSuccess }) => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState(patient.notes || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setNotes(patient.notes || '');
  }, [patient.notes]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await clinicsService.updatePatient(patient.id, { notes });
      onSaveSuccess();
    } catch (err) {
      console.error('Failed to update notes', err);
      alert('Error al guardar las notas.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div data-ui="clinics-patient-notes-modal-region-001" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div data-ui="clinics-patient-notes-modal-region-002" className="bg-black/30 backdrop-filter backdrop-blur-xl border border-white/20 rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div data-ui="clinics-patient-notes-modal-region-003" className="flex justify-between items-center p-6 border-b border-white/20 bg-black/30">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <FontAwesomeIcon icon={faNotesMedical} className="clinics-white-shadow-icon text-blue-400" />
            {t('clinics.chat.patientNotes')}
          </h3>
          <button
            data-ui="clinics-patient-notes-modal-close"
            onClick={onClose}
            className="w-8 h-8 p-0 flex items-center justify-center bg-black rounded-full text-white/60 hover:text-white transition-colors border border-white/10"
          >
            <FontAwesomeIcon icon={faTimes} className="text-sm" />
          </button>
        </div>

        {/* Body */}
        <div data-ui="clinics-patient-notes-modal-region-004" className="p-6 bg-black-800/50 backdrop-blur-[2px]">
          <p className="text-sm text-white/60 mb-4">
            {t('clinics.chat.addOrEditNotes', { name: patient.full_name })}
          </p>
          <textarea
            className="w-full h-64 bg-black/75 border border-white/20 rounded-xl p-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none gemini-scrollbar"
            placeholder={t('clinics.chat.writeNotesHere')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSaving}
          />
        </div>

        {/* Footer */}
        <div data-ui="clinics-patient-notes-modal-region-005" className="flex justify-end gap-3 p-6 border-t border-white/20 bg-black/30">
          <button
            data-ui="clinics-patient-notes-modal-cancel"
            onClick={onClose}
            className="btn-taratic btn-ghost-dark px-4 py-2 border rounded-full"
            disabled={isSaving}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            className="btn-taratic btn-green px-6 py-2 flex items-center gap-2 rounded-full"
            disabled={isSaving}
          >
            {isSaving ? (
              <div data-ui="clinics-patient-notes-modal-region-006" className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin"></div>
            ) : (
              <FontAwesomeIcon icon={faSave} className="clinics-white-shadow-icon" />
            )}
            {t('clinics.chat.saveNotes')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClinicsPatientNotesModal;
